/**
 * PERSON LIST — Lista contatti (nuovo design, port dal mockup): page-head +
 * DataGrid ristilata (ordina/filtra/nascondi/gestisci colonne mantenuti) con
 * celle in stile mockup (avatar+codice, nome, email, telefono, chip stato
 * marketing, azioni). Dati e logica reali invariati.
 */

import { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useServerSearch } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPersons, deletePerson, createPerson, FETCH_ALL_PAGE_SIZE } from './personThunks';
import { rapidContactApi } from '../rapidContact/rapidContactApi';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { useToast } from '../../components/useToast';
import { ImportButton } from '../../components/ImportButton';
import { SearchGlyph, PlusGlyph, ImportGlyph, TrashGlyph, EditGlyph } from '../../components/icons';
import type { MarketingStatus, PersonCreatePayload, Person } from './personTypes';

interface PersonListProps {
  onCreateClick: () => void;
  onEditClick?: (personId: number) => void;
}

// Box Dashboard -> gruppo di marketingStatus da portare in cima alla lista.
const BOX_PRIORITY_GROUPS: Record<string, MarketingStatus[]> = {
  customers: ['customer'],
  qualified: ['marketing_qualified', 'sales_qualified'],
};

// Stato marketing -> chiave i18n della label + classe chip (colori mockup).
const MARKETING_CHIP: Record<string, { key: string; chip: string }> = {
  lead: { key: 'lead', chip: 'neutral' },
  marketing_qualified: { key: 'marketingQualified', chip: 'info' },
  sales_qualified: { key: 'salesQualified', chip: 'soft' },
  customer: { key: 'customer', chip: 'gold' },
};

function initialsOf(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '—';
}

export default function PersonList({ onCreateClick, onEditClick }: PersonListProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const toast = useToast();
  const { items, status, error } = useAppSelector((state) => state.persons);
  const { search, setSearch, query } = useServerSearch();
  const [searchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const box = searchParams.get('box');
  useEffect(() => {
    setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 }));
  }, [query, box]);

  useEffect(() => {
    dispatch(fetchPersons({ pageSize: FETCH_ALL_PAGE_SIZE, search: query || undefined }));
  }, [query, dispatch]);

  // Riordino "gruppo del box in cima" (comparatore stabile).
  const rows = useMemo(() => {
    const group = box ? BOX_PRIORITY_GROUPS[box] : undefined;
    if (!group) return items;
    const inGroup = (s: MarketingStatus) => group.includes(s);
    return [...items].sort(
      (a, b) => Number(inGroup(b.marketingStatus)) - Number(inGroup(a.marketingStatus)),
    );
  }, [items, box]);

  async function handleDelete(personId: number) {
    try {
      await dispatch(deletePerson(personId)).unwrap();
      dispatch(fetchPersons({ pageSize: FETCH_ALL_PAGE_SIZE, search: query || undefined }));
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  // FILO-73: cancellare la persona cancella anche i contatti rapidi delle fiere importati in lei.
  // La modale lo dice solo se ce ne sono, perché la pagina Fiere può essere spenta e una frase fissa
  // sarebbe fuorviante. Se il conteggio non arriva, avvisa comunque: tacere sarebbe peggio.
  async function requestDelete(person: Person) {
    let warning = '';
    try {
      const count = (await rapidContactApi.getByPerson(person.personId)).length;
      if (count > 0) warning = t('person.deleteConfirmRapidContacts', { count });
    } catch {
      warning = t('person.deleteConfirmRapidContactsUnknown');
    }
    const message = t('person.deleteConfirm', { name: `${person.firstName} ${person.lastName}` });
    confirm.requestConfirm(warning ? `${message} ${warning}` : message, () => handleDelete(person.personId));
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('person.title')}</h1>
          <span className="page-sub">{t('common.record', { count: items.length })}</span>
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('person.searchPlaceholder')}
              aria-label={t('person.searchPlaceholder')}
            />
          </div>
          <ImportButton
            entityLabel={t('person.title').toLowerCase()}
            className="btn-ghost"
            icon={<ImportGlyph />}
            onEntities={async (raws) => {
              for (const raw of raws) await dispatch(createPerson(raw as PersonCreatePayload)).unwrap();
            }}
          />
          <button type="button" className="btn-primary" onClick={onCreateClick}>
            <PlusGlyph /> {t('person.newContact')}
          </button>
        </div>
      </div>

      <div className="list-card">
        {status === 'failed' && <p className="list-message">{t('common.error', { message: error })}</p>}
        {status === 'loading' && items.length === 0 && <p className="list-message">{t('common.loading')}</p>}
        {status === 'succeeded' && items.length === 0 && (
          <div className="empty-state"><p>{t('person.noContacts')}</p></div>
        )}

        {items.length > 0 && (
          <DataGrid
            rows={rows}
            columns={personColumns(t, requestDelete, onEditClick)}
            getRowId={(row) => row.personId}
            rowHeight={64}
            autoHeight
            disableRowSelectionOnClick
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            loading={status === 'loading'}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? '' : 'even-row')}
            className="tk-grid"
          />
        )}
      </div>
      <ConfirmDialog open={confirm.isOpen} message={confirm.message} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
    </div>
  );
}

function personColumns(
  t: (key: string, opts?: Record<string, unknown>) => string,
  requestDelete: (person: Person) => void,
  onEditClick?: (personId: number) => void,
): GridColDef<Person>[] {
  return [
    {
      field: 'externalCode', headerName: t('person.columns.externalCode'), width: 140,
      renderCell: (p) => (
        <Link to={`/contacts/${p.row.personId}`} className="lc-code">{(p.value as string) || '—'}</Link>
      ),
    },
    {
      field: 'lastName', headerName: t('person.columns.name'), flex: 1, minWidth: 220,
      valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
      renderCell: (p) => (
        <span className="lc-name">
          <span className="lc-av">{initialsOf(p.row.firstName, p.row.lastName)}</span>
          <Link to={`/contacts/${p.row.personId}`} className="nm">{p.value as string}</Link>
        </span>
      ),
    },
    {
      field: 'email', headerName: t('person.columns.email'), width: 220, sortable: false,
      valueGetter: (_v, row) => row.emailAddresses[0]?.address ?? '—',
      renderCell: (p) => <span className="lc-email">{p.value as string}</span>,
    },
    {
      field: 'phone', headerName: t('person.columns.phone'), width: 160, sortable: false,
      valueGetter: (_v, row) => row.phones[0]?.number ?? '—',
      renderCell: (p) => <span className="lc-phone">{p.value as string}</span>,
    },
    {
      field: 'marketingStatus', headerName: t('person.columns.marketingStatus'), width: 170,
      renderCell: (p) => {
        const cfg = MARKETING_CHIP[p.row.marketingStatus];
        return (
          <span className={`chip ${cfg?.chip ?? 'neutral'}`}>
            {cfg ? t(`person.marketingStatuses.${cfg.key}`) : p.row.marketingStatus}
          </span>
        );
      },
    },
    {
      field: 'actions', headerName: t('person.columns.actions'), sortable: false, filterable: false,
      width: 100, align: 'right', headerAlign: 'right',
      renderCell: (p) => (
        <span className="lc-actions">
          {import.meta.env.VITE_ENABLE_EDIT_FORMS === 'true' && onEditClick && (
            <button type="button" className="lc-act" aria-label={t('common.edit')} onClick={() => onEditClick(p.row.personId)}>
              <EditGlyph />
            </button>
          )}
          <button
            type="button"
            className="lc-act del"
            aria-label={t('common.delete')}
            onClick={() => requestDelete(p.row)}
          >
            <TrashGlyph />
          </button>
        </span>
      ),
    },
  ];
}
